import * as m from '@/paraglide/messages';
import type { NoteRow as Note } from '@listening/frontend/query/notes';
import NotesIcon from '@listening/assets/icons/notes.svg?react';
import ShareIcon from '@listening/assets/icons/share.svg?react';

import { DialogContent } from '@listening/frontend/components/radix/dialog';
import { cn } from '@listening/utils/react-helpers';
import {
  dialogSurface,
  primaryButtonSurface,
  secondaryButtonSurface,
  wellSurface,
} from '@/components/web-fe/design-v2/surfaces';
import { DialogCloseContext } from '@listening/frontend/components/custom/dialog-dropdown';
import { format } from 'date-fns';
import { Page } from 'iconoir-react';
import { use, useState } from 'react';
import { toast } from 'sonner';
import { useBigLogoSrc } from '@/hooks/logo-theme.hooks';
import { getDisplayAudioTitle } from '@/lib/audio-display-metadata';
import { useAtomValue } from '@effect/atom-react';
import { audioSummaryAtom } from '@listening/frontend/query/audio-detail';
import { AsyncResult } from 'effect/reactivity';
import { rootLogger } from '@/lib/root-logger';
import { useAnalytics } from '@listening/frontend/analytics/client';
import { useCopyText } from '@/lib/clipboard';
import { Exit } from 'effect';

const moduleLogger = rootLogger.child({ module: 'ShareNotesDialog' });

/**
 * The well previews the card the note is shared as, so it keeps the document,
 * the note and the Listening mark together. Everything outside it is dialog
 * chrome and is not part of what gets shared.
 */
function ShareNotesDialog({ note }: { note: Note }) {
  const closeContext = use(DialogCloseContext);
  const closeDialog = closeContext.closeDialog;
  const { capture } = useAnalytics();
  const { copy } = useCopyText();
  const audioItem = AsyncResult.getOrElse(
    useAtomValue(audioSummaryAtom(note.audio_conversion_id)),
    () => undefined,
  );
  const bigLogoSrc = useBigLogoSrc();
  const title = getDisplayAudioTitle(audioItem?.title ?? note.title);

  const share = () => {
    capture('share_notes_button_clicked', null);
    // `navigator.share` is absent on most desktop browsers, where calling it
    // throws. Copying is the sensible fallback for a text note.
    if (navigator.share !== undefined) {
      navigator.share({ text: note.body, title }).catch((error: unknown) => {
        // An abort is the user dismissing the sheet, not a failure.
        moduleLogger.info({ error }, 'Share sheet dismissed or failed');
      });
      closeDialog();
      return;
    }
    void copy(note.body).then((exit) => {
      if (Exit.isSuccess(exit)) toast.success(m.notes_v2_share_copied());
      else {
        moduleLogger.error('Failed to copy note to clipboard');
        toast.error(m.notes_v2_share_copy_failed());
      }
      closeDialog();
    });
  };

  return (
    <DialogContent
      className={cn(
        'flex max-h-[85svh] max-w-[28rem] flex-col gap-[var(--v2-gap-4)] p-[var(--v2-gap-4)]',
        dialogSurface,
      )}
    >
      <div
        className={cn(
          'flex min-h-0 flex-col gap-[var(--v2-gap-4)] p-[var(--v2-gap-4)]',
          wellSurface,
        )}
      >
        <div className='flex shrink-0 items-start gap-[var(--v2-gap-3)]'>
          <NoteCover coverImg={audioItem?.cover_img_url ?? null} />
          <div className='flex min-w-0 flex-col gap-1'>
            <p
              className='line-clamp-3 text-[15px] leading-snug text-[var(--v2-text-primary)]'
              style={{ fontWeight: 'var(--v2-heading-weight)' }}
            >
              {title}
            </p>
            <p className='font-mono text-[12px] text-[var(--v2-text-muted)]'>
              {format(new Date(note.updated_at_ms), 'MMM d, yyyy')}
            </p>
          </div>
        </div>

        <p className='min-h-0 overflow-y-auto text-[15px] leading-relaxed whitespace-pre-wrap text-[var(--v2-text-primary)]'>
          {note.body}
        </p>

        <img
          alt='Listening'
          src={bigLogoSrc}
          className='h-5 w-auto shrink-0 self-start object-contain opacity-60'
        />
      </div>

      <div className='flex w-full shrink-0 gap-[var(--v2-gap-2)]'>
        <button
          type='button'
          onClick={() => {
            moduleLogger.info('See on notes button clicked');
            closeDialog();
          }}
          className={cn(
            'flex h-10 grow items-center justify-center gap-2 text-[13px] font-semibold',
            secondaryButtonSurface,
          )}
        >
          <NotesIcon className='size-4 shrink-0' />
          {m.dialog_see_on_notes()}
        </button>
        <button
          type='button'
          onClick={share}
          className={cn(
            'flex h-10 grow items-center justify-center gap-2 text-[13px] font-semibold',
            primaryButtonSurface,
          )}
        >
          <ShareIcon className='size-4 shrink-0' />
          {m.common_share()}
        </button>
      </div>
    </DialogContent>
  );
}

/** Page-shaped, matching how a document reads in the library and notes list. */
function NoteCover({ coverImg }: { coverImg: string | null }) {
  const [hasFailed, setHasFailed] = useState(false);

  return (
    <span
      className='relative flex h-14 shrink-0 items-center justify-center overflow-hidden rounded-[6px] border-[length:var(--v2-border-width)] border-[var(--v2-card-border)] bg-[var(--v2-page-bg)] text-[var(--v2-text-muted)] shadow-[0_1px_2px_rgb(0_0_0/0.07)]'
      style={{ aspectRatio: 'var(--v2-row-cover-ratio)' }}
    >
      {coverImg === null || coverImg === '' || hasFailed ? (
        <Page className='size-5' strokeWidth={1.6} />
      ) : (
        <img
          alt=''
          src={coverImg}
          loading='lazy'
          onError={() => {
            setHasFailed(true);
          }}
          className='absolute inset-0 size-full bg-white object-cover object-top'
        />
      )}
    </span>
  );
}

export default ShareNotesDialog;
